import { useState } from 'react';
import { Highlight, type PrismTheme } from 'prism-react-renderer';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

type Props = {
  code: string;
  language?: 'bash' | 'json' | 'typescript' | 'javascript' | 'python';
  copyable?: boolean;
  /** Cap the block height; content scrolls inside. */
  maxHeight?: number | string;
};

// Colors come from the CSS custom properties so the block follows the
// light/dark color scheme — a hardcoded palette renders ink-on-ink in
// the opposite mode.
const editorialTheme: PrismTheme = {
  plain: { color: 'var(--ink)', backgroundColor: 'transparent' },
  styles: [
    { types: ['string', 'attr-value', 'number', 'boolean'], style: { color: 'var(--accent)' } },
    { types: ['comment'], style: { color: 'var(--ink-muted)', fontStyle: 'italic' as const } },
    { types: ['punctuation', 'operator'], style: { color: 'var(--ink-muted)' } },
    { types: ['property', 'keyword', 'function', 'builtin'], style: { color: 'var(--ink)' } },
  ],
};

export function CodeBlock({ code, language = 'bash', copyable = true, maxHeight }: Props) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid var(--rule)',
        borderRadius: 0.5,
        backgroundColor: 'var(--bg-elevated)',
        my: 2,
      }}
    >
      <Highlight code={code.trim()} language={language} theme={editorialTheme}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <Box
            component="pre"
            className={className}
            style={style}
            sx={{
              m: 0,
              p: 2.5,
              // Keep the first screenful of a long line clear of the copy button.
              pr: copyable ? 7 : 2.5,
              fontFamily: 'IBM Plex Mono',
              fontSize: '0.8125rem',
              lineHeight: 1.6,
              overflow: 'auto',
              whiteSpace: 'pre',
              maxHeight,
            }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </Box>
        )}
      </Highlight>
      {copyable && (
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton
            size="small"
            onClick={onCopy}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              borderRadius: 0,
              border: '1px solid var(--rule)',
              backgroundColor: 'var(--bg)',
              '&:hover': { backgroundColor: 'var(--bg)' },
            }}
          >
            {copied ? <CheckOutlinedIcon fontSize="small" /> : <ContentCopyOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
